import React, { useState, useEffect } from 'react';
import {
  MdSearch,
  MdFileDownload,
  MdCheckCircle,
  MdCancel,
  MdVisibility,
  MdRefresh,
  MdImage,
  MdClose,
  MdWarning
} from 'react-icons/md';
import { FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import './KYCQueue.css';
import { getAllKYCDocuments } from '../../../lib/supabase/helpers';
import { supabase } from '../../../lib/supabase/client';

/* ── Toast ─────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`kq-toast kq-toast-${type}`}>
      <span className="kq-toast-icon">
        {type === 'success' ? <FiCheck /> : type === 'error' ? <FiX /> : <FiAlertCircle />}
      </span>
      <span>{message}</span>
      <button className="kq-toast-close" onClick={onClose}><FiX /></button>
    </div>
  );
}

/* ── Confirm Dialog ─────────────────────────────────────── */
function ConfirmDialog({ title, message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = false }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
        <div className="confirm-icon">{danger ? <MdWarning size={32} /> : <MdCheckCircle size={32} />}</div>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="confirm-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? 'btn-danger' : 'btn-success'}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────── */
export default function KYCQueue() {
  const [kycRequests, setKycRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [selectedKyc, setSelectedKyc] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [imageLabel, setImageLabel] = useState('');
  const [processing, setProcessing] = useState(false);

  // Rejection flow
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Approve confirm dialog
  const [confirmApproveKyc, setConfirmApproveKyc] = useState(null);

  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => { fetchKYCRequests(); }, []);

  /* ── BUG 1 FIX: status calc moved OUTSIDE forEach ─────── */
  const fetchKYCRequests = async () => {
    try {
      setLoading(true);
      const data = await getAllKYCDocuments();

      // Step 1: group documents by user
      const groupedByUser = {};
      data.forEach(doc => {
        if (!groupedByUser[doc.user_id]) {
          groupedByUser[doc.user_id] = {
            id: doc.user_id,
            userId: doc.user_id,
            userName: `${doc.profiles?.first_name || ''} ${doc.profiles?.last_name || ''}`.trim() || 'Unknown User',
            userEmail: doc.profiles?.email || 'N/A',
            phone: doc.profiles?.phone || 'N/A',
            submittedAt: new Date(doc.created_at),
            verifiedBy: doc.verified_by,
            verifiedAt: doc.verified_at ? new Date(doc.verified_at) : null,
            documents: {},
            allDocumentIds: []
          };
        }
        groupedByUser[doc.user_id].documents[doc.document_type] = {
          id: doc.id,
          url: doc.document_url,
          number: doc.document_number,
          status: doc.status  // lowercase: 'approved' | 'pending' | 'rejected'
        };
        groupedByUser[doc.user_id].allDocumentIds.push(doc.id);
      });

      // Step 2: calculate overall status AFTER all documents are grouped
      // FIX: removed the ">= 4" requirement — if ALL submitted docs are approved → APPROVED
      const formattedKYC = Object.values(groupedByUser).map(user => {
        const docStatuses = Object.values(user.documents).map(d => d.status);
        const anyRejected = docStatuses.some(s => s === 'rejected');
        const allApproved = docStatuses.length > 0 && docStatuses.every(s => s === 'approved');

        let status = 'PENDING';
        if (anyRejected) status = 'REJECTED';
        else if (allApproved) status = 'APPROVED';

        return {
          ...user,
          status,
          panNumber: user.documents.pan?.number || 'N/A',
          panDocUrl: user.documents.pan?.url || '',
          aadhaarNumber: user.documents.aadhaar?.number || 'N/A',
          aadhaarDocUrl: user.documents.aadhaar?.url || '',
          selfieUrl: user.documents.selfie?.url || '',
          addressProofUrl: user.documents.proof_of_address?.url || '',
        };
      });

      setKycRequests(formattedKYC);
    } catch (error) {
      console.error('Error fetching KYC requests:', error);
      showToast('Failed to load KYC requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Approve ─────────────────────────────────────────── */
  // BUG 4 FIX: accept kyc object directly, no stale kycRequests.find()
  const handleApprove = async (kyc) => {
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { showToast('You must be logged in', 'error'); return; }

      const docTypes = ['pan', 'aadhaar', 'selfie', 'proof_of_address'];
      const updates = docTypes
        .filter(t => kyc.documents[t])
        .map(t =>
          supabase.from('kyc_documents').update({
            status: 'approved',
            verified_by: user.id,
            verified_at: new Date().toISOString()
          }).eq('id', kyc.documents[t].id)
        );

      const results = await Promise.all(updates);
      const failed = results.filter(r => r.error);
      if (failed.length > 0) {
        showToast(`${failed.length} document(s) failed to approve`, 'error');
        return;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ kyc_status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', kyc.userId);

      if (profileError) {
        showToast('Documents approved but profile status update failed', 'warning');
      } else {
        showToast('KYC approved! User can now access all trading features.', 'success');
      }

      setConfirmApproveKyc(null);
      setShowModal(false);
      await fetchKYCRequests();
    } catch (error) {
      console.error('Error approving KYC:', error);
      showToast('Failed to approve KYC. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  /* ── Reject ──────────────────────────────────────────── */
  const handleReject = async (kyc, reason) => {
    if (!reason?.trim()) { showToast('Please enter a rejection reason', 'error'); return; }
    setProcessing(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { showToast('You must be logged in', 'error'); return; }

      const docTypes = ['pan', 'aadhaar', 'selfie', 'proof_of_address'];
      const updates = docTypes
        .filter(t => kyc.documents[t])
        .map(t =>
          supabase.from('kyc_documents').update({
            status: 'rejected',
            verified_by: user.id,
            verified_at: new Date().toISOString(),
            rejection_reason: reason.trim()
          }).eq('id', kyc.documents[t].id)
        );

      const results = await Promise.all(updates);
      const failed = results.filter(r => r.error);
      if (failed.length > 0) {
        showToast(`${failed.length} document(s) failed to reject`, 'error');
        return;
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({ kyc_status: 'rejected', updated_at: new Date().toISOString() })
        .eq('id', kyc.userId);

      if (profileError) {
        showToast('Documents rejected but profile status update failed', 'warning');
      } else {
        showToast('KYC rejected. User has been notified.', 'success');
      }

      setShowRejectInput(false);
      setRejectReason('');
      setShowModal(false);
      await fetchKYCRequests();
    } catch (error) {
      console.error('Error rejecting KYC:', error);
      showToast('Failed to reject KYC. Please try again.', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewDetails = (kyc) => {
    setSelectedKyc(kyc);
    setShowRejectInput(false);
    setRejectReason('');
    setShowModal(true);
  };

  const handleViewImage = (url, label = 'Document') => {
    setSelectedImage(url);
    setImageLabel(label);
    setShowImageModal(true);
  };

  const handleExport = () => {
    const headers = ['User Name', 'Email', 'Phone', 'PAN', 'Aadhaar', 'Status', 'Submitted Date'];
    const rows = filteredKyc.map(kyc => [
      kyc.userName, kyc.userEmail, kyc.phone,
      kyc.panNumber, kyc.aadhaarNumber,
      kyc.status, kyc.submittedAt.toLocaleDateString()
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kyc_requests_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredKyc = kycRequests.filter(kyc => {
    const q = searchTerm.toLowerCase();
    const matchSearch =
      kyc.userName.toLowerCase().includes(q) ||
      kyc.userEmail.toLowerCase().includes(q) ||
      kyc.panNumber.toLowerCase().includes(q) ||
      kyc.aadhaarNumber.includes(searchTerm);
    const matchStatus = statusFilter === 'ALL' || kyc.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = {
    pending: kycRequests.filter(k => k.status === 'PENDING').length,
    approved: kycRequests.filter(k => k.status === 'APPROVED').length,
    rejected: kycRequests.filter(k => k.status === 'REJECTED').length,
    total: kycRequests.length
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Loading KYC requests…</p>
      </div>
    );
  }

  return (
    <div className="kyc-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Approve Confirm Dialog */}
      {confirmApproveKyc && (
        <ConfirmDialog
          title="Approve KYC?"
          message={`Approve all documents for ${confirmApproveKyc.userName}? They will gain full trading access.`}
          confirmLabel={processing ? 'Approving…' : 'Yes, Approve'}
          onConfirm={() => handleApprove(confirmApproveKyc)}
          onCancel={() => setConfirmApproveKyc(null)}
        />
      )}

      {/* Header */}
      <div className="kyc-header">
        <div>
          <h1>KYC Verification Queue</h1>
          <p>Review and verify client KYC documents</p>
        </div>
        <button className="btn btn-primary" onClick={fetchKYCRequests} disabled={loading}>
          <MdRefresh size={18} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="kyc-stats">
        <div className="stat-card-small pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-info"><p>Pending Review</p><h3>{stats.pending}</h3></div>
        </div>
        <div className="stat-card-small approved">
          <div className="stat-icon">✅</div>
          <div className="stat-info"><p>Approved</p><h3>{stats.approved}</h3></div>
        </div>
        <div className="stat-card-small rejected">
          <div className="stat-icon">❌</div>
          <div className="stat-info"><p>Rejected</p><h3>{stats.rejected}</h3></div>
        </div>
        <div className="stat-card-small total">
          <div className="stat-icon">📋</div>
          <div className="stat-info"><p>Total Submissions</p><h3>{stats.total}</h3></div>
        </div>
      </div>

      {/* Filters */}
      <div className="kyc-filters">
        <div className="search-box">
          <MdSearch size={20} />
          <input
            type="text"
            placeholder="Search by name, email, PAN, Aadhaar…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <button className="btn btn-success" onClick={handleExport}>
          <MdFileDownload size={18} /> Export CSV
        </button>
      </div>

      <div className="results-info">
        Showing <strong>{filteredKyc.length}</strong> of <strong>{kycRequests.length}</strong> KYC requests
      </div>

      {/* Table */}
      <div className="kyc-table-container">
        <table className="kyc-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Client</th>
              <th>PAN</th>
              <th>Aadhaar</th>
              <th>Documents</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredKyc.length === 0 ? (
              <tr><td colSpan="8" className="no-data">No KYC requests found</td></tr>
            ) : filteredKyc.map(kyc => (
              <tr key={kyc.id}>
                <td><span className="kyc-id">#{kyc.id.substring(0, 8)}</span></td>
                <td>
                  <div className="client-info-small">
                    <div className="client-name">{kyc.userName}</div>
                    <div className="client-email">{kyc.userEmail}</div>
                    <div className="client-phone">{kyc.phone}</div>
                  </div>
                </td>
                <td><span className="doc-number">{kyc.panNumber}</span></td>
                <td><span className="doc-number">{kyc.aadhaarNumber}</span></td>
                <td>
                  <div className="document-previews">
                    {kyc.panDocUrl && (
                      <button className="doc-preview-btn" onClick={() => handleViewImage(kyc.panDocUrl, 'PAN Card')}>
                        <MdImage size={14} /> PAN
                      </button>
                    )}
                    {kyc.aadhaarDocUrl && (
                      <button className="doc-preview-btn" onClick={() => handleViewImage(kyc.aadhaarDocUrl, 'Aadhaar')}>
                        <MdImage size={14} /> Aadhaar
                      </button>
                    )}
                    {kyc.selfieUrl && (
                      <button className="doc-preview-btn" onClick={() => handleViewImage(kyc.selfieUrl, 'Selfie')}>
                        <MdImage size={14} /> Selfie
                      </button>
                    )}
                    {kyc.addressProofUrl && (
                      <button className="doc-preview-btn" onClick={() => handleViewImage(kyc.addressProofUrl, 'Address Proof')}>
                        <MdImage size={14} /> Address
                      </button>
                    )}
                  </div>
                </td>
                <td><span className={`status-badge status-${kyc.status.toLowerCase()}`}>{kyc.status}</span></td>
                <td>{kyc.submittedAt.toLocaleDateString('en-IN')}</td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon btn-view" title="View Details" onClick={() => handleViewDetails(kyc)}>
                      <MdVisibility size={17} />
                    </button>
                    {kyc.status === 'PENDING' && (
                      <>
                        <button className="btn-icon btn-approve" title="Approve" onClick={() => setConfirmApproveKyc(kyc)} disabled={processing}>
                          <MdCheckCircle size={17} />
                        </button>
                        <button className="btn-icon btn-reject" title="Reject" onClick={() => { handleViewDetails(kyc); setShowRejectInput(true); }} disabled={processing}>
                          <MdCancel size={17} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Details Modal */}
      {showModal && selectedKyc && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>KYC Verification Details</h2>
                <span className={`status-badge status-${selectedKyc.status.toLowerCase()}`}>
                  {selectedKyc.status}
                </span>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}><MdClose size={22} /></button>
            </div>

            <div className="modal-body">
              <div className="kyc-details-grid">
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{selectedKyc.userName}</span></div>
                  <div className="detail-row"><span className="detail-label">Email</span><span className="detail-value">{selectedKyc.userEmail}</span></div>
                  <div className="detail-row"><span className="detail-label">Phone</span><span className="detail-value">{selectedKyc.phone}</span></div>
                  <div className="detail-row"><span className="detail-label">Submitted</span><span className="detail-value">{selectedKyc.submittedAt.toLocaleString('en-IN')}</span></div>
                </div>
                <div className="detail-section">
                  <h3>Document Numbers</h3>
                  <div className="detail-row"><span className="detail-label">PAN</span><span className="detail-value doc-highlight">{selectedKyc.panNumber}</span></div>
                  <div className="detail-row"><span className="detail-label">Aadhaar</span><span className="detail-value doc-highlight">{selectedKyc.aadhaarNumber}</span></div>
                  {selectedKyc.verifiedAt && (
                    <div className="detail-row"><span className="detail-label">Verified On</span><span className="detail-value">{selectedKyc.verifiedAt.toLocaleString('en-IN')}</span></div>
                  )}
                </div>
              </div>

              {/* Document Images */}
              <div className="detail-section">
                <h3>Uploaded Documents</h3>
                <div className="documents-grid">
                  {selectedKyc.panDocUrl && (
                    <div className="document-card" onClick={() => handleViewImage(selectedKyc.panDocUrl, 'PAN Card')}>
                      <div className="document-label">PAN Card</div>
                      <img src={selectedKyc.panDocUrl} alt="PAN Card" />
                    </div>
                  )}
                  {selectedKyc.aadhaarDocUrl && (
                    <div className="document-card" onClick={() => handleViewImage(selectedKyc.aadhaarDocUrl, 'Aadhaar')}>
                      <div className="document-label">Aadhaar Card</div>
                      <img src={selectedKyc.aadhaarDocUrl} alt="Aadhaar" />
                    </div>
                  )}
                  {selectedKyc.selfieUrl && (
                    <div className="document-card" onClick={() => handleViewImage(selectedKyc.selfieUrl, 'Selfie')}>
                      <div className="document-label">Selfie</div>
                      <img src={selectedKyc.selfieUrl} alt="Selfie" />
                    </div>
                  )}
                  {selectedKyc.addressProofUrl && (
                    <div className="document-card" onClick={() => handleViewImage(selectedKyc.addressProofUrl, 'Address Proof')}>
                      <div className="document-label">Address Proof</div>
                      <img src={selectedKyc.addressProofUrl} alt="Address Proof" />
                    </div>
                  )}
                </div>
              </div>

              {/* Rejection reason input */}
              {showRejectInput && selectedKyc.status === 'PENDING' && (
                <div className="reject-reason-box">
                  <label>Rejection Reason <span style={{ color: '#ef4444' }}>*</span></label>
                  <textarea
                    rows={3}
                    placeholder="Enter reason (will be shared with the user)…"
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                  />
                </div>
              )}
            </div>

            {selectedKyc.status === 'PENDING' && (
              <div className="modal-footer">
                {!showRejectInput ? (
                  <>
                    <button className="btn btn-success" onClick={() => setConfirmApproveKyc(selectedKyc)} disabled={processing}>
                      <MdCheckCircle size={18} /> Approve KYC
                    </button>
                    <button className="btn btn-danger" onClick={() => setShowRejectInput(true)} disabled={processing}>
                      <MdCancel size={18} /> Reject KYC
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleReject(selectedKyc, rejectReason)}
                      disabled={processing || !rejectReason.trim()}
                    >
                      <MdCancel size={18} /> {processing ? 'Rejecting…' : 'Confirm Rejection'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => { setShowRejectInput(false); setRejectReason(''); }}>
                      Cancel
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal" onClick={e => e.stopPropagation()}>
            <div className="image-modal-header">
              <span>{imageLabel}</span>
              <button className="image-modal-close" onClick={() => setShowImageModal(false)}>
                <MdClose size={20} />
              </button>
            </div>
            <img src={selectedImage} alt={imageLabel} />
          </div>
        </div>
      )}
    </div>
  );
}